// Home.js
import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Avatar,
  Fade,
  Chip,
  useTheme,
  Select,
  MenuItem,
} from "@mui/material";
import { AuthContext } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import config from "../config/dashboardConfig.json";
import {
  Assignment as AssignmentIcon,
  PlayArrow as PlayArrowIcon,
  Payment as PaymentIcon,
  Notifications as NotificationsIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

/**
 * Helper function to fetch the board ID from Trello using the member username
 * and the board name. It calls the endpoint:
 *
 *   GET /1/members/{memberUsername}/boards
 *
 * and then finds the board whose name matches (case-insensitive).
 */
async function fetchBoardId(memberUsername, boardName) {
  const apiKey = "18d50606d419c001df43c639bef31408";
  const apiToken = "f847d5371155e21120eb8c5f1ef5b57a22fa331789125143db766cbd147290b3";
  const response = await fetch(
    `https://api.trello.com/1/members/${memberUsername}/boards?key=${apiKey}&token=${apiToken}`
  );
  if (!response.ok) {
    throw new Error(`Error fetching boards: ${response.status}`);
  }
  const boards = await response.json();
  const matchingBoard = boards.find(
    (b) => b.name.trim().toLowerCase() === boardName.trim().toLowerCase()
  );
  return matchingBoard ? matchingBoard.id : null;
}

const Home = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();

  const [boards, setBoards] = useState([]);
  const [selectedBoardName, setSelectedBoardName] = useState("");
  const [selectedMemberUsername, setSelectedMemberUsername] = useState("");
  const [cardCounts, setCardCounts] = useState({});
  const [updates, setUpdates] = useState([]);
  const [trelloMemberId, setTrelloMemberId] = useState(null);

  // Load user configuration on mount.
  useEffect(() => {
    if (user && user.email) {
      const userConfig = config.users.find(
        (u) => u.email.toLowerCase() === user.email.toLowerCase()
      );
      if (userConfig && userConfig.boards && userConfig.boards.length > 0) {
        setBoards(userConfig.boards);
        // Initialize with the first board's details.
        setSelectedBoardName(userConfig.boards[0].boardName);
        setSelectedMemberUsername(userConfig.boards[0].memberUsername);
        setUpdates(config.updates || []);
      }
    }
  }, [user]);

  // Find the current board configuration based on boardName and memberUsername.
  const boardConfig = boards.find(
    (b) =>
      b.boardName === selectedBoardName &&
      b.memberUsername === selectedMemberUsername
  );

  // Fetch the Trello member ID using the memberUsername.
  useEffect(() => {
    async function fetchTrelloMemberId() {
      if (boardConfig && boardConfig.memberUsername) {
        try {
          const apiKey = "18d50606d419c001df43c639bef31408";
          const apiToken = "f847d5371155e21120eb8c5f1ef5b57a22fa331789125143db766cbd147290b3";
          const response = await fetch(
            `https://api.trello.com/1/members/${boardConfig.memberUsername}?key=${apiKey}&token=${apiToken}`
          );
          if (!response.ok) {
            throw new Error(`Error fetching member: ${response.status}`);
          }
          const memberData = await response.json();
          setTrelloMemberId(memberData.id);
        } catch (error) {
          console.error("Failed to fetch Trello member ID:", error);
        }
      }
    }
    fetchTrelloMemberId();
  }, [boardConfig]);

  // Fetch real card counts from Trello by:
  // 1. Using boardName/memberUsername to get the board ID.
  // 2. Fetching the board's lists.
  // 3. Matching each configured list by name.
  // 4. Fetching cards for that list and filtering by the Trello member ID (if available).
  useEffect(() => {
    async function fetchAllCounts() {
      if (!boardConfig) return;
      const newCounts = {};
      const apiKey = "18d50606d419c001df43c639bef31408";
      const apiToken = "f847d5371155e21120eb8c5f1ef5b57a22fa331789125143db766cbd147290b3";

      if (!apiKey || !apiToken) {
        console.error("Trello API key or token is not defined.");
        return;
      }

      let boardId = null;
      try {
        boardId = await fetchBoardId(
          boardConfig.memberUsername,
          boardConfig.boardName
        );
      } catch (error) {
        console.error("Error fetching board ID:", error);
      }
      if (!boardId) {
        console.error("Board not found for boardName:", boardConfig.boardName);
        Object.keys(boardConfig.lists).forEach((key) => {
          newCounts[key] = 0;
        });
        setCardCounts(newCounts);
        return;
      }

      // Cache for board lists
      let boardLists = null;
      try {
        const response = await fetch(
          `https://api.trello.com/1/boards/${boardId}/lists?key=${apiKey}&token=${apiToken}`
        );
        if (!response.ok) {
          throw new Error(`Error fetching board lists: ${response.status}`);
        }
        boardLists = await response.json();
      } catch (error) {
        console.error("Error fetching board lists:", error);
        Object.keys(boardConfig.lists).forEach((key) => {
          newCounts[key] = 0;
        });
        setCardCounts(newCounts);
        return;
      }

      // For each list defined in the configuration, find the matching Trello list by name.
      const promises = Object.entries(boardConfig.lists).map(async ([key, listValue]) => {
        // For external lists we expect a Trello URL is provided,
        // but we will ignore that for board lookup—we use boardName instead.
        // We match the list by its display name.
        const matchingList = boardLists.find(
          (list) =>
            list.name.trim().toLowerCase() === listValue.name.trim().toLowerCase()
        );
        if (!matchingList) {
          console.error(
            `List with name "${listValue.name}" not found on board "${boardConfig.boardName}"`
          );
          newCounts[key] = 0;
          return;
        }
        // Fetch cards for the matched list.
        try {
          const response = await fetch(
            `https://api.trello.com/1/lists/${matchingList.id}/cards?key=${apiKey}&token=${apiToken}`
          );
          if (!response.ok) {
            throw new Error(`Error fetching cards for list ${matchingList.id}: ${response.status}`);
          }
          let cards = await response.json();
          // Filter cards by member if trelloMemberId is available.
          if (trelloMemberId) {
            cards = cards.filter((card) => card.idMembers.includes(trelloMemberId));
          }
          newCounts[key] = Array.isArray(cards) ? cards.length : 0;
        } catch (error) {
          console.error(`Error fetching cards for list ${matchingList.id}:`, error);
          newCounts[key] = 0;
        }
      });
      await Promise.all(promises);
      setCardCounts(newCounts);
    }
    fetchAllCounts();
  }, [boardConfig, trelloMemberId]);

  // Return a status color based on the count.
  const getStatusColor = (count) => {
    if (count === 0) return "success";
    if (count <= 3) return "warning";
    return "error";
  };

  // A reusable TaskCard component.
  const TaskCard = ({ title, count, icon, link, isExternal }) => (
    <Fade in timeout={500}>
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
          },
          height: "100%",
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
              {icon}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          <Chip label={`${count} Pending`} color={getStatusColor(count)} sx={{ alignSelf: "flex-start", mb: 2 }} />
          <Box sx={{ mt: "auto" }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              component={isExternal ? "a" : Link}
              href={isExternal ? link : undefined}
              to={!isExternal ? link : undefined}
              target={isExternal ? "_blank" : undefined}
              endIcon={isExternal ? <OpenInNewIcon /> : null}
              sx={{ borderRadius: 2, textTransform: "none", py: 1 }}
            >
              {isExternal ? "View in Trello" : "View Details"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  // Icon mapping for list keys.
  const iconMapping = {
    todo: <AssignmentIcon />,
    doing: <PlayArrowIcon />,
    signPayment: <PaymentIcon />,
  };

  // Render loading/error messages as needed.
  if (!user) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Typography variant="h5">Loading user data...</Typography>
      </Box>
    );
  }
  if (boards.length === 0 || !boardConfig) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Typography variant="h5">No board configuration found for your account.</Typography>
      </Box>
    );
  }

  // Compute unique board names and member usernames for the dropdowns.
  const uniqueBoardNames = [...new Set(boards.map((b) => b.boardName))];
  const memberOptions = boards
    .filter((b) => b.boardName === selectedBoardName)
    .map((b) => b.memberUsername);
  const uniqueMemberOptions = [...new Set(memberOptions)];

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, backgroundColor: "#f8faff", minHeight: "100vh" }}>
      {/* Header Section */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          Welcome Back, {user.displayName?.split(" ")[0] || user.email}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", mt: { xs: 2, sm: 0 } }}>
          {/* Board Name Dropdown */}
          <Select
            value={selectedBoardName}
            onChange={(e) => {
              const newBoardName = e.target.value;
              setSelectedBoardName(newBoardName);
              // Update member selection to the first available member for the new board.
              const newMembers = boards
                .filter((b) => b.boardName === newBoardName)
                .map((b) => b.memberUsername);
              const uniqueNewMembers = [...new Set(newMembers)];
              setSelectedMemberUsername(uniqueNewMembers[0] || "");
            }}
            size="small"
            sx={{ mr: 2 }}
          >
            {uniqueBoardNames.map((boardName, index) => (
              <MenuItem key={index} value={boardName}>
                {boardName}
              </MenuItem>
            ))}
          </Select>
          {/* Member Username Dropdown */}
          <Select
            value={selectedMemberUsername}
            onChange={(e) => setSelectedMemberUsername(e.target.value)}
            size="small"
            sx={{ mr: 2 }}
          >
            {uniqueMemberOptions.map((member, index) => (
              <MenuItem key={index} value={member}>
                {member}
              </MenuItem>
            ))}
          </Select>
          <IconButton color="primary" size="large">
            <NotificationsIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tasks Overview Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: "text.primary" }}>
          Tasks Overview
        </Typography>
        <Grid container spacing={3}>
          {Object.entries(boardConfig.lists).map(([listKey, listValue]) => {
            const count = cardCounts[listKey] !== undefined ? cardCounts[listKey] : 0;
            // For "signPayment", use internal routing; for other lists, use the provided link.
            const isExternal = listKey !== "signPayment";
            const link =
              listKey === "signPayment"
                ? listValue.link || "/sign-payment"
                : listValue.trelloLink || "#";
            const icon = iconMapping[listKey] || <AssignmentIcon />;
            return (
              <Grid item xs={12} sm={6} md={4} key={listKey}>
                <TaskCard title={listValue.name} count={count} icon={icon} link={link} isExternal={isExternal} />
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Updates Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: "text.primary" }}>
          Latest Updates & Announcements
        </Typography>
        <Grid container spacing={3}>
          {updates.map((update, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Fade in timeout={500} style={{ transitionDelay: `${index * 100}ms` }}>
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": { transform: "translateY(-4px)" },
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "primary.main" }}>
                      {update.title}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mb: 2, color: "text.secondary" }}>
                      {update.date}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.6 }}>
                      {update.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Home;
